import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { apiFetch } from "../apiFetch";
import type { Event } from "../types/event";

interface ActiveEventState {
  eventId: number | null;
  selectedTrackId: string | null;
  updatedAt: string | null;
}

async function fetchActiveEvent(): Promise<ActiveEventState> {
  const res = await apiFetch(`/api/events/active`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<ActiveEventState>;
}

async function putActiveEvent(eventId: number | null): Promise<ActiveEventState> {
  const res = await apiFetch(`/api/events/active`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<ActiveEventState>;
}

async function putEventSelectedTrack(
  eventId: number,
  selectedTrackId: string | null,
): Promise<Event> {
  const res = await apiFetch(`/api/events/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selectedTrackId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Event>;
}

interface ActiveEventContextValue {
  activeEventId: number | null;
  selectedTrackId: string | null;
  /** Last load/save failure, for the UI to surface; null when healthy. */
  error: string | null;
  setActiveEventId: (id: number | null) => void;
  setSelectedTrackId: (id: string | null) => void;
}

const ActiveEventContext = createContext<ActiveEventContextValue>({
  activeEventId: null,
  selectedTrackId: null,
  error: null,
  setActiveEventId: () => {},
  setSelectedTrackId: () => {},
});

export function ActiveEventProvider({ children }: { children: ReactNode }) {
  const [activeEventId, setActiveEventIdState] = useState<number | null>(null);
  const [selectedTrackId, setSelectedTrackIdState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestVersionRef = useRef(0);

  useEffect(() => {
    const requestVersion = ++requestVersionRef.current;

    void (async () => {
      try {
        const data = await fetchActiveEvent();
        if (requestVersion === requestVersionRef.current) {
          setActiveEventIdState(data.eventId);
          setSelectedTrackIdState(data.selectedTrackId);
          setError(null);
        }
      } catch (err) {
        console.error("[active-event] Failed to load active event:", err);
        setError("Could not load the active event; the track map may fall back to GPS-only.");
      }
    })();
  }, []);

  const setActiveEventId = useCallback((id: number | null) => {
    const requestVersion = ++requestVersionRef.current;

    void (async () => {
      try {
        const data = await putActiveEvent(id);
        if (requestVersion === requestVersionRef.current) {
          setActiveEventIdState(data.eventId);
          setSelectedTrackIdState(data.selectedTrackId);
          setError(null);
        }
      } catch (err) {
        console.error("[active-event] Failed to save active event:", err);
        setError("Active event change was not saved.");
      }
    })();
  }, []);

  const setSelectedTrackId = useCallback((id: string | null) => {
    if (activeEventId === null) {
      setSelectedTrackIdState(id);
      return;
    }

    const requestVersion = ++requestVersionRef.current;

    void (async () => {
      try {
        const event = await putEventSelectedTrack(activeEventId, id);
        if (requestVersion === requestVersionRef.current) {
          setSelectedTrackIdState(event.selected_track_id ?? null);
          setError(null);
        }
      } catch (err) {
        console.error("[active-event] Failed to save selected track:", err);
        setError("Track selection was not saved.");
      }
    })();
  }, [activeEventId]);

  return (
    <ActiveEventContext.Provider value={{
      activeEventId,
      selectedTrackId,
      error,
      setActiveEventId,
      setSelectedTrackId,
    }}>
      {children}
    </ActiveEventContext.Provider>
  );
}

export function useActiveEvent() {
  return useContext(ActiveEventContext);
}
